import React from 'react';
import { useGoogleAuth } from '../services/googleAuth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './GoogleCalendar.css';

const GoogleCalendarButton = ({ onEventsImported }) => {
  const { isAuthenticated, signIn, signOut, isLoading, user, refreshToken } = useGoogleAuth();
  const [isImporting, setIsImporting] = React.useState(false);
  const [hasAutoImported, setHasAutoImported] = React.useState(false);
  
  // Disabled: Auto import when authenticated and set up periodic imports
  // React.useEffect(() => {
  //   // Initial auto-import when authenticated
  //   const autoImportEvents = async () => {
  //     // Check if user is authenticated and we're not currently importing
  //     if (isAuthenticated && !isImporting) {
  //       console.log('Auto importing Google Calendar events...');
  //       await importEvents();
  //       setHasAutoImported(true);
  //     }
  //   };
  //   
  //   // Run initial auto-import
  //   autoImportEvents();
  //   
  //   // Set up interval for periodic imports (every 60 seconds)
  //   let intervalId = null;
  //   if (isAuthenticated) {
  //     intervalId = setInterval(async () => {
  //       if (!isImporting) { // Prevent overlapping imports
  //         console.log('Periodic check for new Google Calendar events...');
  //         await importEvents();
  //       }
  //     }, 60000); // 60000 ms = 1 minute
  //   }
  //   
  //   // Clean up interval when component unmounts or auth state changes
  //   return () => {
  //     if (intervalId) {
  //       clearInterval(intervalId);
  //     }
  //   };
  // }, [isAuthenticated]);
  
  const handleImportEvents = async () => {
    if (!isAuthenticated) {
      // Check if we should use backend auth
      const useBackendAuth = localStorage.getItem('preferBackendAuth') === 'true';
      
      if (useBackendAuth) {
        try {
          console.log('🔄 Using backend authentication...');
          const functions = getFunctions();
          const initAuth = httpsCallable(functions, 'initGoogleAuth');
          const result = await initAuth();
          window.location.href = result.data.authUrl;
        } catch (error) {
          console.error('Backend auth failed, falling back to frontend:', error);
          signIn();
        }
      } else {
        signIn();
      }
      return;
    }
    
    // Reset auto-import flag when manually importing
    setHasAutoImported(false);
    importEvents();
  };
  
  // Separate the import logic so it can be called from both handlers
  const importEvents = async () => {
    try {
      setIsImporting(true);
      
      // Import calendar utilities
      const { fetchGoogleCalendarEvents, convertGoogleEvents } = await import('../services/googleCalendar');
      
      // Get events from today to next year
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0); // Set to beginning of today
      
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
      
      // Get token from storage
      const token = localStorage.getItem('googleAccessToken');
      if (!token) {
        throw new Error('No Google access token found. Please sign in again.');
      }
      
      try {
        // Fetch events from Google Calendar API
        const googleEvents = await fetchGoogleCalendarEvents(
          token,
          startDate.toISOString(),
          endDate.toISOString()
        );
        
        // Get current user ID for converting events
        let currentUserId = null;
        try {
          // Try to get the current user ID from auth or localStorage
          const { auth } = await import('../firebase');
          
          currentUserId = auth.currentUser?.uid;
          
          // Fallback to localStorage if Firebase auth not available
          if (!currentUserId) {
            currentUserId = localStorage.getItem('userId');
          }
        } catch (e) {
          console.warn('Could not get user ID for event conversion:', e);
        }
        
        // Convert Google Calendar events to app format with user ID
        const convertedEvents = convertGoogleEvents(googleEvents, currentUserId);
        
        if (convertedEvents.length === 0) {
          console.log('No events found in Google Calendar for the selected time range.');
          return;
        }
        
        // Check for duplicates by comparing with existing events
        const uniqueEvents = await checkForDuplicateEvents(convertedEvents);
        
        // Show success message with count
        if (uniqueEvents.length > 0) {
          console.log(`Importing ${uniqueEvents.length} unique events from Google Calendar (filtered from ${convertedEvents.length} total)`);
          
          // Pass the converted events back to the parent component
          if (onEventsImported) {
            onEventsImported(uniqueEvents);
          }
          
          // Only show alert if user manually triggered the import
          if (!hasAutoImported) {
            alert(`Successfully imported ${uniqueEvents.length} unique events from Google Calendar (${convertedEvents.length - uniqueEvents.length} duplicates were skipped).`);
          }
        } else {
          // Only show alert if user manually triggered the import
          if (!hasAutoImported) {
            alert('All events from Google Calendar already exist in your app. No new events were imported.');
          }
        }
      } catch (apiError) {
        console.error('API Error:', apiError);
        
        // Check for specific errors
        if (apiError.message === 'TOKEN_EXPIRED') {
          console.log('Token expired, attempting to refresh...');
          
          try {
            // First try to refresh the token silently
            const newToken = await refreshToken();
            
            if (newToken) {
              console.log('Successfully refreshed token, retrying import...');
              // Retry the import with the new token
              await importEvents();
              return;
            } else {
              console.log('Failed to refresh token silently, will try normal sign in');
              // If silent refresh fails but we're auto-importing, just retry sign in without alerts
              if (!hasAutoImported) {
                alert('Your Google Calendar session needs to be refreshed. Reconnecting automatically...');
              }
              
              // Try to sign in again (with minimal UI)
              signIn();
              return;
            }
          } catch (refreshError) {
            console.error('Error during token refresh:', refreshError);
            // If refresh completely fails and this was user-initiated, show a friendly message
            if (!hasAutoImported) {
              alert('Unable to refresh your Google Calendar session. Please try connecting again.');
            }
            return;
          }
        }
        else if (apiError.message && apiError.message.includes('insufficient authentication scopes')) {
          // For scope issues we do need to sign out and re-authenticate with full consent
          if (!hasAutoImported) {
            alert('You need additional permissions to access your calendar. Please reconnect your account.');
          }
          
          // Sign out and then prompt for sign in with full consent
          signOut();
          setTimeout(() => {
            signIn(); // Start sign-in again with full consent
          }, 1000);
          return;
        }
        
        // Rethrow to be caught by outer catch
        throw apiError;
      }
    } catch (error) {
      console.error('Error importing Google Calendar events:', error);
      // Only show alert if user manually triggered the import
      if (!hasAutoImported) {
        alert(`Failed to import events from Google Calendar: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsImporting(false);
    }
  };
  
  // Function to check for duplicate events in Firebase
  const checkForDuplicateEvents = async (googleEvents) => {
    try {
      if (!googleEvents || googleEvents.length === 0) return [];
      
      // Get current user ID from authentication
      let currentUserId = null;
      try {
        // Try to get the current user ID from localStorage or auth context
        const { auth } = await import('../firebase');
        
        currentUserId = auth.currentUser?.uid;
        
        // Fallback to localStorage if Firebase auth not available
        if (!currentUserId) {
          currentUserId = localStorage.getItem('userId');
        }
        
        console.log('Current user ID for duplicate check:', currentUserId);
      } catch (userError) {
        console.error('Error getting current user ID:', userError);
        // Continue without user ID - will rely on other duplicate criteria
      }
      
      // Get current events from Firebase via props or context
      // We can ask the parent component to provide these
      let existingEvents = [];
      
      try {
        // Use Firebase to fetch existing events
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const eventsCollection = collection(db, 'acceptedEvents');
        
        let querySnapshot;
        
        // If we have a user ID, only check for duplicates among this user's events
        if (currentUserId) {
          const userEventsQuery = query(eventsCollection, where("userId", "==", currentUserId));
          querySnapshot = await getDocs(userEventsQuery);
          console.log(`Fetching events for user ${currentUserId} only`);
        } else {
          // If no user ID, fetch all events (fallback to original behavior)
          querySnapshot = await getDocs(eventsCollection);
          console.log('No user ID available, fetching all events for duplicate check');
        }
        
        existingEvents = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          firestoreId: doc.id
        }));
        
        console.log(`Found ${existingEvents.length} existing events in Firebase`);
      } catch (error) {
        console.error('Error fetching existing events from Firebase:', error);
        // Continue with empty existing events - better to risk duplication than block import
        return googleEvents;
      }
      
      // Filter out duplicate events
      const uniqueEvents = googleEvents.filter(googleEvent => {
        // Add user ID to new events from Google Calendar
        if (currentUserId) {
          googleEvent.userId = currentUserId;
        }
        
        // Check if this Google event already exists in Firebase
        const isDuplicate = existingEvents.some(existingEvent => {
          // First, if both events have user IDs and they're different, they're not duplicates
          if (existingEvent.userId && googleEvent.userId && 
              existingEvent.userId !== googleEvent.userId) {
            return false;
          }
          
          // Now check other duplication criteria
          // 1. If it has a Google ID and it matches
          if (existingEvent.googleId && existingEvent.googleId === googleEvent.googleId) {
            return true;
          }
          
          // 2. Check if the name, date, and time match
          const sameDate = 
            existingEvent.day === googleEvent.day &&
            existingEvent.month === googleEvent.month &&
            existingEvent.year === googleEvent.year;
            
          const sameTime =
            (existingEvent.isAllDay && googleEvent.isAllDay) ||
            (existingEvent.hour === googleEvent.hour && 
             existingEvent.minute === googleEvent.minute);
             
          const sameName = existingEvent.eventName === googleEvent.eventName;
          
          if (sameDate && sameTime && sameName) {
            return true;
          }
          
          return false;
        });
        
        // Keep events that aren't duplicates
        return !isDuplicate;
      });
      
      console.log(`After duplicate filtering: ${uniqueEvents.length} events remaining`);
      return uniqueEvents;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return googleEvents; // In case of error, return all events
    }
  };
  
  if (isLoading) {
    return (
      <button 
        className="google-calendar-button loading"
        disabled
        style={buttonStyle}
      >
        <div className="spinner" style={spinnerStyle}></div>
        Loading...
      </button>
    );
  }
  
  return (
    <div className="google-calendar-container" style={containerStyle}>
      <button 
        className={`google-calendar-button ${isAuthenticated ? 'connected' : ''}`}
        onClick={handleImportEvents}
        disabled={isImporting}
        style={{
          ...buttonStyle,
          backgroundColor: isAuthenticated ? '#1a73e8' : '#ffffff',
          color: isAuthenticated ? '#ffffff' : '#444444',
        }}
      >
        {isImporting ? (
          <>
            <div className="spinner" style={spinnerStyle}></div>
            Importing Events...
          </>
        ) : (
          <>
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" 
              alt="Google Calendar" 
              style={iconStyle} 
            />
            {isAuthenticated 
              ? 'Import Google Calendar Events'
              : 'Connect Google Calendar'}
          </>
        )}
      </button>
      
      {isAuthenticated && (
        <>
          <div className="google-auth-info" style={userInfoStyle}>
            <div style={userDetail}>
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  style={userImageStyle} 
                />
              )}
              <span style={userNameStyle}>{user?.name || 'Google User'}</span>
            </div>
            <button 
              onClick={signOut} 
              style={signOutButtonStyle}
            >
              Disconnect
            </button>
          </div>
          
        </>
      )}
    </div>
  );
};

// Styles
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '15px 0',
  width: '100%',
};

const buttonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #dadce0',
  fontSize: '15px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  height: '44px',
  width: '100%',
  maxWidth: '280px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const iconStyle = {
  width: '20px',
  height: '20px',
  marginRight: '10px',
};

const spinnerStyle = {
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: '2px solid #f3f3f3',
  borderTop: '2px solid #1a73e8',
  animation: 'spin 1s linear infinite',
  marginRight: '10px',
};

const userInfoStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '280px',
  padding: '8px 0',
  marginTop: '8px',
};

const userDetail = {
  display: 'flex',
  alignItems: 'center',
};

const userImageStyle = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  marginRight: '8px',
};

const userNameStyle = {
  fontSize: '14px',
  color: '#444444',
};

const signOutButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#1a73e8',
  fontSize: '14px',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
};

export default GoogleCalendarButton;